import { useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { WordsService } from "./words-service";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  useColorModeValue,
  useDisclosure,
  useToast,
  Select,
  Badge,
  VStack,
  Input,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";

import { Table, TableRowActions, Panel, PageHeading } from "@bemap/ui";

import { Word } from "./words-service";
import { useTranslation } from "react-i18next";

const WordsManagement = () => {
  const toast = useToast()
  const { t } = useTranslation()
  const initialWord: Omit<Word, 'id' | 'createdAt' | 'updatedAt'> = {
    order: 0,
    word: '',
    word_es: '',
    word_en: '',
    profileType: 'E',
    category: 'positive',
    isActive: true
  }

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">();
  const [words, setWords] = useState<Word[]>([]);
  const [word, setWord] = useState<Word | Omit<Word, 'id' | 'createdAt' | 'updatedAt'>>(initialWord);
  const [updateData, setUpdateData] = useState<string>("");

  const getProfileTypeLabel = (type: string) => {
    switch (type) {
      case 'E': return t('words.profile_type.executor');
      case 'C': return t('words.profile_type.communicator');
      case 'A': return t('words.profile_type.analyst');
      case 'P': return t('words.profile_type.planner');
      default: return type;
    }
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'positive': return t('words.category.positive');
      case 'negative': return t('words.category.negative');
      default: return category;
    }
  }

  const columns = useMemo<ColumnDef<Word, any>[]>(
    () => [
      {
        accessorFn: (row) => row.order,
        id: "Order",
        header: t('words.table.order'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.word,
        id: "Word",
        header: t('words.table.word'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.word_es,
        id: "Word ES",
        header: t('words.table.word_es'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.word_en,
        id: "Word EN",
        header: t('words.table.word_en'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.profileType,
        id: "Profile Type",
        header: t('words.table.profile_type'),
        cell: (info) => (
          <Badge colorScheme={info.getValue() === 'E' ? 'blue' : info.getValue() === 'C' ? 'green' : info.getValue() === 'A' ? 'purple' : 'orange'}>
            {getProfileTypeLabel(info.getValue())}
          </Badge>
        ),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.category,
        id: "Category",
        header: t('words.table.category'),
        cell: (info) => (
          <Badge colorScheme={info.getValue() === 'positive' ? 'green' : 'red'}>
            {getCategoryLabel(info.getValue())}
          </Badge>
        ),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.isActive,
        id: "Status",
        header: t('words.table.status'),
        cell: (info) => (
          <Badge colorScheme={info.getValue() ? 'green' : 'red'}>
            {info.getValue() ? t('words.status.active') : t('words.status.inactive')}
          </Badge>
        ),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.createdAt,
        id: "Created At",
        header: t('words.table.created_at'),
        cell: (info) => formatDate(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.id,
        id: "Actions",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row) => {
          return (
            <TableRowActions editAction={() => editWord(row.getValue())} deleteAction={() => openDeleteDialog(row.getValue())} />
          );
        },
      },
    ],
    [t]
  );

  const background = useColorModeValue("transparent", "transparent");
  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  useEffect(() => {
    loadWords();
  }, [updateData]);

  function closeModal() {
    onClose()
    setWord(initialWord)
  }

  const formatDate = (date: string | Date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  async function loadWords() {
    setIsLoading(true);
    try {
      const response = await WordsService.getWords();
      setWords(response.message);
      setIsLoading(false);
    } catch (error) {
      setWords([]);
      setIsLoading(false);
    }
  }

  async function editWord(wordId: string) {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const req = await WordsService.getWordById(wordId);

      toast.closeAll()
      setWord(req.message);
      setIsLoading(false);
      setModalType("edit");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function openDeleteDialog(wordId: string) {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const req = await WordsService.getWordById(wordId);

      toast.closeAll()
      setWord(req.message);
      setModalType("delete");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function createNewWord() {
    setIsLoading(true);
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await WordsService.createWord(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>);
      setIsLoading(false)
      setIsSaving(false)
      closeModal()
      setUpdateData(new Date().toLocaleString())
    } catch (error) {
      toast.closeAll();
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  const handleUpdateWord = async (payload: Word) => {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await WordsService.updateWord(payload);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteWord = async () => {
    toast({
      title: t('general.deleting'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await WordsService.deleteWord((word as Word).id);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error) {
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  async function handleChange(event: any) {
    setWord({
      ...word,
      [event.target.name]: event.target.value,
    });
  }

  function RenderModal() {
    switch (modalType) {
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('words.edit_title')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.order')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="order"
                      placeholder={t('words.form.order_placeholder')}
                      type="number"
                      onChange={handleChange}
                      value={(word as Word)?.order || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.word')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="word"
                      placeholder={t('words.form.word_placeholder')}
                      type="text"
                      onChange={handleChange}
                      value={(word as Word)?.word || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.word_es')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="word_es"
                      placeholder={t('words.form.word_es_placeholder')}
                      type="text"
                      onChange={handleChange}
                      value={(word as Word)?.word_es || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.word_en')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="word_en"
                      placeholder={t('words.form.word_en_placeholder')}
                      type="text"
                      onChange={handleChange}
                      value={(word as Word)?.word_en || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.profile_type')}</FormLabel>
                    <Select
                      name="profileType"
                      value={(word as Word)?.profileType || "E"}
                      onChange={handleChange}
                      placeholder={t('words.form.profile_type_placeholder')}
                    >
                      <option value="E">{t('words.profile_type.executor')}</option>
                      <option value="C">{t('words.profile_type.communicator')}</option>
                      <option value="A">{t('words.profile_type.analyst')}</option>
                      <option value="P">{t('words.profile_type.planner')}</option>
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.category')}</FormLabel>
                    <Select
                      name="category"
                      value={(word as Word)?.category || "positive"}
                      onChange={handleChange}
                      placeholder={t('words.form.category_placeholder')}
                    >
                      <option value="positive">{t('words.category.positive')}</option>
                      <option value="negative">{t('words.category.negative')}</option>
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.status')}</FormLabel>
                    <Select
                      name="isActive"
                      value={(word as Word)?.isActive ? "true" : "false"}
                      onChange={(e) => setWord({ ...word, isActive: e.target.value === "true" })}
                      placeholder={t('words.form.status_placeholder')}
                    >
                      <option value="true">{t('words.status.active')}</option>
                      <option value="false">{t('words.status.inactive')}</option>
                    </Select>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={() => handleUpdateWord(word as Word)} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('words.new_title')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.order')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="order"
                      placeholder={t('words.form.order_placeholder')}
                      type="number"
                      onChange={handleChange}
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.order || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.word')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="word"
                      placeholder={t('words.form.word_placeholder')}
                      type="text"
                      onChange={handleChange}
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.word || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.word_es')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="word_es"
                      placeholder={t('words.form.word_es_placeholder')}
                      type="text"
                      onChange={handleChange}
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.word_es || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.word_en')}</FormLabel>
                    <Input
                      color={textColor}
                      background={background}
                      name="word_en"
                      placeholder={t('words.form.word_en_placeholder')}
                      type="text"
                      onChange={handleChange}
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.word_en || ""}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.profile_type')}</FormLabel>
                    <Select
                      name="profileType"
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.profileType || "E"}
                      onChange={handleChange}
                      placeholder={t('words.form.profile_type_placeholder')}
                    >
                      <option value="E">{t('words.profile_type.executor')}</option>
                      <option value="C">{t('words.profile_type.communicator')}</option>
                      <option value="A">{t('words.profile_type.analyst')}</option>
                      <option value="P">{t('words.profile_type.planner')}</option>
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.category')}</FormLabel>
                    <Select
                      name="category"
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.category || "positive"}
                      onChange={handleChange}
                      placeholder={t('words.form.category_placeholder')}
                    >
                      <option value="positive">{t('words.category.positive')}</option>
                      <option value="negative">{t('words.category.negative')}</option>
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>{t('words.table.status')}</FormLabel>
                    <Select
                      name="isActive"
                      value={(word as Omit<Word, 'id' | 'createdAt' | 'updatedAt'>)?.isActive ? "true" : "false"}
                      onChange={(e) => setWord({ ...word, isActive: e.target.value === "true" })}
                      placeholder={t('words.form.status_placeholder')}
                    >
                      <option value="true">{t('words.status.active')}</option>
                      <option value="false">{t('words.status.inactive')}</option>
                    </Select>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewWord} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('words.delete_title')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('words.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteWord} ml={3}>
                    {t('actions.delete')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
    }
  }

  return (
    <>
      <VStack gap="20px" w="100%" align="stretch">
        <PageHeading title={t('words.title')} actions={[{
          label: t('words.add_new'),
          onClick: () => {
            setWord(initialWord);
            setModalType("new");
            onOpen();
          }
        }]} />

        <Panel>
          {isLoading ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
            </Stack>
          ) : (
            words && (
              <Table
                tableId="admin-words-table"
                columns={columns}
                data={words}
              />
            )
          )}
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default WordsManagement; 